import { action, computed, makeObservable, observable, override } from 'mobx';

import { ILocalStore } from '@/stores/interfaces/ILocalStore';
import { PopupStore } from '@/stores/local/shared/PopupStore';
import { formatDuration } from '@/utils/formatDuration';

import type { ToolCallHistorySnapshot, ToolCallHistoryStep, ToolCallHistoryStepText } from './types';

type PrivateFields = '_steps' | '_startedAt' | '_finishedAt' | '_currentMessageId';

export class ToolCallHistoryStore extends PopupStore<string | null> implements ILocalStore {
  private _steps: ToolCallHistoryStep[] = [];
  private _startedAt: number | null = null;
  private _finishedAt: number | null = null;
  private _seq = 0;
  private _currentMessageId: string | null = null;

  constructor() {
    super();

    makeObservable<ToolCallHistoryStore, PrivateFields>(this, {
      _steps: observable.shallow,
      _startedAt: observable,
      _finishedAt: observable,
      _currentMessageId: observable,
      steps: computed,
      hasSteps: computed,
      isFinished: computed,
      durationLabel: computed,
      summaryLabel: computed,
      shouldShowSummary: computed,
      currentMessageId: computed,
      activeMessageId: computed,
      start: action.bound,
      addStep: action.bound,
      addToolCallStep: action.bound,
      finish: action.bound,
      bindToMessage: action.bound,
      clearCurrentMessage: action.bound,
      openForMessage: action.bound,
      close: override,
      reset: action.bound,
    });
  }

  get steps(): ToolCallHistoryStep[] {
    return this._steps;
  }

  get hasSteps(): boolean {
    return this._steps.length > 0;
  }

  get isFinished(): boolean {
    return this._finishedAt != null;
  }

  get durationLabel(): string | null {
    if (this._startedAt == null || this._finishedAt == null) {
      return null;
    }

    return formatDuration(this._finishedAt - this._startedAt);
  }

  get summaryLabel(): string | null {
    if (!this.isFinished) {
      return null;
    }

    return this.durationLabel ? `Ответ подготовлен за ${this.durationLabel}` : 'Ответ подготовлен';
  }

  get shouldShowSummary(): boolean {
    return Boolean(this.summaryLabel);
  }

  get currentMessageId(): string | null {
    return this._currentMessageId;
  }

  get activeMessageId(): string | null {
    return this.entity ? String(this.entity) : null;
  }

  snapshot(): ToolCallHistorySnapshot {
    return {
      steps: [...this._steps],
      durationLabel: this.durationLabel,
      summaryLabel: this.summaryLabel,
    };
  }

  start(): void {
    if (this._startedAt == null) {
      this._startedAt = Date.now();
    }
  }

  addToolCallStep(toolCallId: string, displayName: string, content: string): void {
    const trimmedName = displayName.trim();
    const trimmedContent = content.trim();

    if (!trimmedName && !trimmedContent) {
      return;
    }

    this.start();

    const nextStep: ToolCallHistoryStep = {
      id: toolCallId,
      kind: 'tool_interaction',
      displayName: trimmedName || 'Действие',
      resultContent: trimmedContent,
      createdAt: Date.now(),
    };

    this._steps = [...this._steps, nextStep];
  }

  addStep(kind: ToolCallHistoryStepText['kind'], text: string): void {
    const trimmed = text.trim();

    if (!trimmed) {
      return;
    }

    this.start();

    if (kind === 'status') {
      const last = this._steps[this._steps.length - 1];
      const lastText = last && 'text' in last ? (last.text?.trim().toLowerCase() ?? '') : '';

      if (last?.kind === 'result' && 'text' in last && lastText.startsWith('собрал всю информацию')) {
        this._steps = this._steps.slice(0, -1);
      }
    }

    this._seq += 1;
    const nextStep: ToolCallHistoryStepText = {
      id: `${this._seq}`,
      text: trimmed,
      kind,
      createdAt: Date.now(),
    };

    const lastStep = this._steps[this._steps.length - 1];

    // Если добавляются источники информации, а последний шаг - результат,
    // вставляем источники перед результатом, чтобы сохранить правильный порядок:
    // сначала источники, затем результат
    if (kind === 'information_sources' && lastStep?.kind === 'result') {
      const updated = [...this._steps];

      updated.splice(updated.length - 1, 0, nextStep);
      this._steps = updated;

      return;
    }

    this._steps = [...this._steps, nextStep];
  }

  finish(): void {
    if (this._startedAt == null) {
      this._startedAt = Date.now();
    }

    this._finishedAt = Date.now();
  }

  bindToMessage(messageId: string | null): void {
    this._currentMessageId = messageId ? String(messageId) : null;
  }

  clearCurrentMessage(): void {
    this._currentMessageId = null;
  }

  openForMessage(messageId: string | null): void {
    this.open(messageId ? String(messageId) : null);
  }

  close(): void {
    this.entity = null;
    super.close();
  }

  reset(): void {
    this._steps = [];
    this._startedAt = null;
    this._finishedAt = null;
    this._seq = 0;
    this._currentMessageId = null;
    this.entity = null;
    super.close();
  }

  destroy(): void {
    super.destroy();
  }
}
